import { useEffect, useState } from "react";
import React from "react";
import { useQuery } from "@tanstack/react-query";
import { getGalleryInner } from "../Service/Api";
import { useNavigate } from "react-router-dom";
import LazyLoad from "react-lazyload";
import Breadcrumb from "../Component/Breadcrumb";
import Footer from "../Component/Footer";
import Header from "../Component/Header";
import Menu from "../Component/Menu";
import PageTitle from "../Component/PageTitle";

const Gallery = () => {
  const navigate = useNavigate();
  const [data, setData] = useState([]);
  const [filterData, setFilteredData] = useState([]);
  const [selectedYear, setSelectedYear] = useState();
  const [selectedMonth, setSelectedMonth] = useState("All");
  const [monthOptions, setMonthOptions] = useState(["All"]);
  const [yearRanges, setYearRanges] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [totalPages, setTotalPages] = useState(1);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [limit] = useState(100000);

  const monthNames = [
    "Jan",
    "Feb",
    "Mar",
    "Apr",
    "May",
    "Jun",
    "Jul",
    "Aug",
    "Sep",
    "Oct",
    "Nov",
    "Dec",
  ];

  useEffect(() => {
    const currentYear = new Date().getFullYear();
    setSelectedYear(currentYear.toString());
  }, []);

  const convertDateToDDMMYYYY = (isoString) => {
    const date = new Date(isoString);
    const day = String(date.getDate()).padStart(2, "0");
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const year = date.getFullYear();
    return `${day}-${month}-${year}`;
  };

  const latestYear = new Date().getFullYear().toString();

  const { data: latestYearData = [] } = useQuery({
    queryKey: ["gallery-latest-year", latestYear, currentPage, limit],
    queryFn: async () => {
      const galleryData1 = await getGalleryInner({
        page: currentPage,
        limit,
        year: latestYear,
      });
      const galleryData = galleryData1.filter(
        (item) => item.category === "PhotoGallery"
      );

      return galleryData.map((item) => ({
        ...item,
        formattedDate: item.date ? convertDateToDDMMYYYY(item.date) : null,
      }));
    },
    placeholderData: (previousData) => previousData,
    staleTime: 5 * 60 * 1000,
    gcTime: 30 * 60 * 1000,
    refetchOnWindowFocus: true,
  });

  const { data: allYearsData = [] } = useQuery({
    queryKey: ["gallery-years", currentPage, limit],
    queryFn: async () => {
      const galleryData1 = await getGalleryInner({
        page: currentPage,
        limit,
        year: "",
      });
      return galleryData1.filter((item) => item.category === "PhotoGallery");
    },
    placeholderData: (previousData) => previousData,
    staleTime: 5 * 60 * 1000,
    gcTime: 30 * 60 * 1000,
    refetchOnWindowFocus: true,
  });

  useEffect(() => {
    if (!allYearsData) {
      return;
    }

    const mergedData = [...latestYearData, ...allYearsData].reduce(
      (acc, item) => {
        if (!acc.map.has(item._id)) {
          acc.map.set(item._id, true);
          acc.items.push(item);
        }
        return acc;
      },
      { items: [], map: new Map() }
    ).items;

    setData(mergedData);

    const yearsSet = new Set();
    const monthsSet = new Set();

    const yearSource = allYearsData.length ? allYearsData : mergedData;

    yearSource.forEach((item) => {
      if (item.date) {
        const date = new Date(item.date);
        yearsSet.add(date.getFullYear());
        monthsSet.add(date.getMonth());
      }
    });

    const uniqueYearRanges = Array.from(yearsSet).sort((a, b) => b - a);
    setYearRanges(uniqueYearRanges);

    const monthOptions = Array.from(monthsSet)
      .sort((a, b) => a - b)
      .map((month) => monthNames[month]);
    setMonthOptions(["All", ...monthOptions]);
    setTotalPages(1);
    setCurrentPage(1);
    setTotalCount(mergedData.length);
  }, [latestYearData, allYearsData]);

  useEffect(() => {
    let filteredData = data;

    if (selectedYear !== "All") {
      filteredData = filteredData.filter((item) => {
        const itemDate = item.date?.trim();
        if (itemDate) {
          const date = new Date(itemDate);
          const year = date.getFullYear();
          const month = date.getMonth();
          return (
            year === parseInt(selectedYear) &&
            (selectedMonth === "All" || monthNames[month] === selectedMonth)
          );
        }
        return false;
      });
    } else if (selectedMonth !== "All") {
      filteredData = filteredData.filter((item) => {
        const itemDate = item.date?.trim();
        if (itemDate) {
          const month = new Date(itemDate).getMonth();
          return monthNames[month] === selectedMonth;
        }
        return false;
      });
    }

    if (searchQuery) {
      filteredData = filteredData.filter((item) =>
        item.title.toLowerCase().includes(searchQuery.toLowerCase())
      );
    }

    // Sort the filtered data by date in descending order
    filteredData.sort((a, b) => new Date(b.date) - new Date(a.date));

    setFilteredData(filteredData);
  }, [selectedYear, selectedMonth, searchQuery, data]);

  const handleModal = (id) => {
    navigate(`/SubGallery?id=${id}`);
  };

  const emptyArray = [
    {
      description:
        "“Educating the mind without educating the heart is no education at all.” Sacred Heart has been educating boys and girls since 1990; though much has changed over the years our mission remains the same. Sacred Heart is committed to developing students of high academic achievement, intellectual curiosity, and strong moral character. As a result, year after year Sacred Heart sends our respectful, responsible and compassionate young adults, who are well prepared to tackle the challenges that lay ahead. Since arriving at Sacred Heart in 2016, I have had the opportunity to observe this unique institution up close. I have seen its commitment to academic excellence, its dynamic student body, and incredibly committed faculty and a loving community where the students and families are proud and supportive of one another. As I walk down the hallways of the school every day, I can hear the chatter of eager minds; the shouts of excitement from the victorious athletes, the thumb of dancers feed and the sound of melodious voices harmonizing the perpetual energy, movement and enthusiasm permeate the atmosphere. Ours is a school with difference. We value creativity and innovation and strive to nurture them in our students. Sacred Heartians are groomed to become confident, articulate and enlightened young citizens ready to step into the global society with dignity, honour and integrity. Aristotle once said “Educating the mind without educating the heart is no education at all”. Even as we impart education to match the advancement in technology and globalization, we march our children ahead with Sacred Heart ethos of moral values and principles. We Endeavour constantly to instill these qualities in our children. We pride ourselves to help them grow and develop into sensitive, responsible God loving citizens of the future. May the Sacred Heart of Jesus bless us abundantly in all our endeavourers.",
      attachments: ["director.jpg"],
    },
  ];
  const homeMenu = [
    { label: "Awards & Achievements", link: "/awardsAcheivements" },
    {
      label: "Gallery",
      link: "/Gallery",
      subMenu: [
        { label: "Photo Gallery", link: "/Gallery" },
        { label: "Video Gallery", link: "/VideoGallery" },
      ],
    },

    {
      label: "Careers",
      link: "/careers-openings",
      subMenu: [{ label: "Philosophy", link: "/philosophy" }],
    },
    { label: "School Calendar", link: "/calender" },
    {
      label: "Alumni",
      link: "",
      subMenu: [
        { label: "Admissions", link: "/admission" },
        { label: "Admissions IIT", link: "/admissionIIT" },
      ],
    },

    { label: "eMagazine", link: "/Magazine" },
    { label: "TAABIR Digital Dreams", link: "/digitaldreams" },
    {
      label: "CUET",
      link: "https://webapi.entab.info/api/image/LVISGURGAON/public/pdf/CUET.pdf",
    },
  ];

  return (
    <>
      <Header />
      <Breadcrumb breadImage="https://webapi.entab.info/api/image/LVISGURGAON/public/Images/banner-gallery.jpg" />
      <Menu MenuItem={homeMenu} />
      <div className="padding-blk inner-page">
        <div className="container-fluid">
          <div className="row">
            <div className="col-md-12">
              <PageTitle
                pageTitle="Gallery"
                breadEnd="Gallery"
                breadMid="Gallery"
              />
            </div>
          </div>
          <div className="row">
            <div className="col-xxl-12 col-xl-12 col-lg-12 col-12">
              <div className="month-selection float-end d-block">
                <select
                  value={selectedYear}
                  onChange={(e) => setSelectedYear(e.target.value)}
                  className="yearSelection">
                  <option value="All">All</option>
                  {yearRanges.map((year) => (
                    <option key={year} value={year}>
                      {year}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </div>
          <div className="row years year2023 showYear">
            {filterData.length > 0
              ? filterData.map((item, index) => {
                  const date = new Date(item.date);
                  const day = date.getDate();
                  const year = date.getFullYear();
                  const month = monthNames[date.getMonth()];
                  return (
                    <div
                      className="col-xxl-3 col-xl-3 col-lg-6 col-md-6 col-12 galleryCount"
                      key={index}>
                      <div className="gal_new">
                        <a
                          onClick={() =>
                            navigate(`/SubGallery?id=${item._id}`)
                          }>
                          <article>
                            <h6 className="date">
                              {" "}
                              {day} {month} {year}{" "}
                            </h6>
                            <LazyLoad>
                              <img
                                src={`https://webapi.entab.info/api/image/${item?.attachments[0]}`}
                                alt="Lotus Valley school Gurgaon"
                                className="img-fluid"
                              />
                            </LazyLoad>
                            <div className="gal-content">
                              <h4 className="catgry-name">{item.title}</h4>
                            </div>
                          </article>
                        </a>
                      </div>
                    </div>
                  );
                })
              : emptyArray.map((index) => (
                  <div
                    key={index}
                    className="col-xxl-3 col-xl-3 col-lg-6 col-md-6 col-12 galleryCount">
                    <div className="gal_new">
                      <article>
                        <LazyLoad>
                          <img
                            src="https://webapi.entab.info/api/image/LVISGURGAON/public/Images/one-img.jpg"
                            alt="Lotus Valley school Gurgaon"
                            className="img-fluid"
                          />
                        </LazyLoad>
                        <div className="gal-content">
                          <h4 className="catgry-name">Category Name</h4>
                          <h6 className="date"> DD MM YYYY</h6>
                        </div>
                      </article>
                    </div>
                  </div>
                ))}
          </div>
        </div>
      </div>
      <Footer />
    </>
  );
};

export default Gallery;
